import { useContext, useState } from 'react'
import { NVButton } from '@nv/basic-components'
import { Loader } from '@nv/basic-components'
import { TextField } from '@nv/basic-components'
import { Colon } from '@nv/components'
import { OnboardProgressBar, PasswordValidate } from '@nv/components'
import { LoginFlowContext, useVoterUserContext } from '@nv/contexts'
import { OnlineVoterAuthService } from '@nv/services'
import { nxConfig } from '@nv/utils'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import styles from './OnBoardingCreatePassword.module.scss'
import '@djthoms/pretty-checkbox'

const initialValues = {
  password: '',
  confirmPassword: '',
}

export function OnBoardingCreatePassword() {
  const { t } = useTranslation()

  const LOGIN_CONTEXT = useContext(LoginFlowContext)
  const [loading, setLoading] = useState(false)
  const [validationResult, setValidationResult] = useState(false)
  const [serverError, setServerError] = useState(false)
  const VoterUser = useVoterUserContext().voterUser

  const { setVoterUser } = useVoterUserContext()

  const passwordSchema = Yup.object().shape({
    password: Yup.string().required(t('auth.password.change.validations.newReq')),
    confirmPassword: Yup.string()
      .required(t('auth.password.change.validations.confirmReq'))
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf([Yup.ref('password')], t('auth.password.change.validations.match')),
      }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setServerError(false)
      setTimeout(async () => {
        const password = values.password
        const confirmPassword = values.confirmPassword

        const userInput = {
          password,
          confirmPassword,
        }

        const resp = await OnlineVoterAuthService.createNewPassword(userInput.password)
        if (resp.user) {
          setVoterUser(resp.user)
        }
        LOGIN_CONTEXT.setLoginFlow('Login')
        setLoading(false)
        if (resp.error) {
          setServerError(true)
        }
      }, 10)
    },
  })

  const UserInfo = ({ username, phone }) => {
    return (
      <div className={styles['user-info']}>
        <span className={styles['user-info__greeting']}>
          {t('main.hi')}, <span className={clsx(styles['user-info__greeting'], styles['user-info__greeting--primary'])}>{username}</span>
        </span>

        <div className={styles['user-info__phone']}>
          <span className={styles['user-info__phone__label']}>
            {t('form.labels.phoneNumber')}
            <Colon />
          </span>
          <span className={styles['user-info__phone__value']}>{phone}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles['container']}>
      <div className={styles['form']}>
        <OnboardProgressBar currentStep={4} />

        <UserInfo username={VoterUser.username} phone={VoterUser.phone} />

        <form
          className={styles['form-credentials']}
          onSubmit={(e) => {
            e.preventDefault()
            formik.submitForm()
          }}
        >
          <span className={styles['form-credentials__title']}>{t('auth.password.create.title')}</span>

          <TextField
            classes={{ container: styles['form-credentials__password'] }}
            formik={formik}
            type='password'
            field='password'
            placeholder={t('auth.password.change.labels.new')}
            label={t('auth.password.change.labels.new')}
            isRequired={true}
            isPassword={true}
          />

          <TextField
            classes={{ container: styles['form-credentials__confirm-pass'] }}
            formik={formik}
            type='password'
            field='confirmPassword'
            placeholder={t('auth.password.change.labels.confirmNew')}
            label={t('auth.password.change.labels.confirmNew')}
            isRequired={true}
            isPassword={true}
          />

          <PasswordValidate
            view={'voter'}
            formik={formik}
            result={(value) => {
              setValidationResult(value)
            }}
          />

          {serverError && (
            <div className={styles['error']}>
              {nxConfig.supportEmail
                ? t('errors.contactSupportEmail', {
                    supportEmail: nxConfig.supportEmail,
                  })
                : t('errors.contactSupport')}
            </div>
          )}
        </form>

        <div className={styles['footer']}>
          {loading ? (
            <Loader />
          ) : (
            <NVButton
              type='button'
              text={t('buttons.save')}
              disabled={!validationResult || formik.values.confirmPassword !== formik.values.password}
              onClick={() => formik.submitForm()}
              height={50}
            />
          )}
        </div>
      </div>
    </div>
  )
}
